import React from "react";
import { ChevronDown, ChevronRight, Loader } from "lucide-react";
import { useCollapsed } from "../../Treasury/hooks/useCollapsed";
import { useAutoConversionLimits, useAutoConversionPartners, useSetAutoConversion } from "../hooks/useAutoConversion";
import AutoConversionAttempts from "./AutoConversionAttempts";
import AutoConversionLimits from "./AutoConversionLimits";

// A payout is rejected when the partner's wallet is short. With this on for
// a partner, the payout first tops the wallet up from the partner's own USD,
// USDT or USDC (before anything is debited), then carries on. Off for every
// partner until ticked here. Only partners that are balance-checked before a
// payout and have a conversion endpoint can be ticked.
function AutoConversionPanel() {
  const { data, isLoading, isError, error } = useAutoConversionPartners();
  const { mutate: setAutoConversion, isPending, variables } = useSetAutoConversion();
  const { collapsed, toggle } = useCollapsed("payout-partner-auto-conversion");
  const { data: limits } = useAutoConversionLimits();
  const enabledCount = (data || []).filter((row) => row.enabled).length;

  const handleToggle = (row) => {
    const enabled = !row.enabled;
    if (
      enabled &&
      !window.confirm(
        `Switch on automatic conversion for ${row.partner}?\n\nWhen a payout would fail because ${row.partner}'s wallet is short, real money will be converted from ${row.partner}'s own USD, USDT or USDC wallet to cover it (up to ${limits?.maxUsd ?? 100} USD per conversion and ${limits?.dailyMaxUsd ?? 500} USD per day across all partners). Untick it to stop at any time.`,
      )
    ) {
      return;
    }
    setAutoConversion({ partner: row.partner, enabled });
  };

  return (
    <div className={`mb-6 rounded-xl border border-gray-200 bg-gray-50/50 ${collapsed ? "p-3" : "p-4"}`}>
      <button
        onClick={toggle}
        aria-expanded={!collapsed}
        title={collapsed ? "Show automatic conversion" : "Hide automatic conversion"}
        className={`flex gap-2 items-center text-sm font-bold text-[#013941] ${collapsed ? "" : "mb-1"}`}
      >
        {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        Automatic conversion
        {collapsed && data && (
          <span
            className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
              enabledCount > 0 ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-600"
            }`}
          >
            {enabledCount > 0 ? `${enabledCount} on` : "all off"}
          </span>
        )}
      </button>
      {!collapsed && (
        <>
      <p className="mb-3 text-sm text-gray-600">
        When a payout would fail because the wallet of a payout partner is short, top that wallet up from the
        partner&apos;s own USD, USDT or USDC just before the payout is processed. Off for every partner until switched on
        here.
      </p>

      <AutoConversionLimits />

      {isLoading && (
        <div className="flex gap-2 items-center text-sm text-gray-500">
          <Loader className="w-4 h-4 animate-spin" /> Loading partners...
        </div>
      )}
      {isError && <p className="text-sm text-red-600">Error: {error.message}</p>}

      {data && (
        <ul className="divide-y divide-gray-100">
          {data.map((row) => (
            <li key={row.partner} className="flex gap-3 justify-between items-center py-2">
              <div>
                <p className="text-sm font-medium text-gray-700 capitalize">{row.partner}</p>
                {!row.canConvert && <p className="text-xs text-gray-400">{row.reason}</p>}
              </div>
              <label className={`flex gap-2 items-center text-sm ${row.canConvert ? "cursor-pointer" : "opacity-40"}`}>
                <input
                  type="checkbox"
                  checked={row.enabled}
                  disabled={!row.canConvert || (isPending && variables?.partner === row.partner)}
                  onChange={() => handleToggle(row)}
                  className="w-4 h-4 accent-emerald-600"
                />
                {row.enabled ? "On" : "Off"}
              </label>
            </li>
          ))}
        </ul>
      )}
      <AutoConversionAttempts />
        </>
      )}
    </div>
  );
}

export default AutoConversionPanel;
